import { publicConfig } from "@/configs/public-config";
import { TokenService } from "@/providers/tokens/tokens.service";
import {
  buildFetchError,
  getFilenameFromContentDisposition as getFilenameFromContentDispositionShared,
  parseApiError,
  readErrorResponseBody,
} from "@/shared/helpers/api-file-download.helper";
import { type IApiResponseError } from "@/shared/interfaces/api-response.interface";

import type { SampleDataKey } from "./sample-data.interface";

export interface IFetchSampleFileResult {
  data?: { blob: Blob; filename: string };
  error?: IApiResponseError;
}

const SAMPLE_DATA_FILENAME_FALLBACKS: Record<SampleDataKey, string> = {
  schoolList: "school_list.csv",
  capacity: "schools_capacities.csv",
  studentFallSnapshot: "student_fall_snapshot.csv",
  studentMonthlyData: "student_monthly_data.csv",
  attendanceZones: "schools_attendance_zones.zip",
  newHousingData: "new_housing_projects.csv",
  newHousingBoundaries: "new_housing_project_boundaries.zip",
};

export async function fetchSampleFile(key: SampleDataKey): Promise<IFetchSampleFileResult> {
  const baseUrl = `${publicConfig.apiUrl}/${publicConfig.apiTag}`;
  const url = `${baseUrl}/sample-data/${key}`;
  const accessToken = TokenService.getAccessToken();
  const headers: HeadersInit = {};
  if (accessToken) {
    headers[publicConfig.authHeader] = `Bearer ${accessToken}`;
  }

  try {
    const response = await fetch(url, { method: "GET", headers });

    if (!response.ok) {
      const errorBody = await readErrorResponseBody(response);
      return { error: parseApiError(response, errorBody) };
    }

    const arrayBuffer = await response.arrayBuffer();
    const blobType = response.headers.get("Content-Type") ?? "application/octet-stream";
    const blob = new Blob([arrayBuffer], { type: blobType });
    const contentDisposition = response.headers.get("Content-Disposition");
    const filename = getFilenameFromContentDispositionShared(
      contentDisposition,
      SAMPLE_DATA_FILENAME_FALLBACKS[key],
    );
    return { data: { blob, filename } };
  } catch (err) {
    return { error: buildFetchError(url, err) };
  }
}
